// Card Effects - Bubbles & Snow - Performance-optimiert mit erweiterten Parametern
import React, { useRef, useEffect, memo } from 'react';
import { DevicePerformance } from '../../utils/performance.js';

// Card Bubbles Effect
export const CardBubblesEffect = memo(function CardBubblesEffect({ effectConfig = {} }) {
  const containerRef = useRef(null);
  const bubblesCreated = useRef(false);
  
  useEffect(() => {
    if (!containerRef.current || bubblesCreated.current || DevicePerformance.prefersReducedMotion) return;
    bubblesCreated.current = true;
    
    const container = containerRef.current;
    const speed = effectConfig.speed || 50;
    const customIcon = effectConfig.customIconUrl;
    const manualIcon = effectConfig.manualIcon;
    
    // Icon-Mapping
    const iconMap = {
      snowflake: '❄️',
      bubble: '🫧',
      heart: '❤️',
      star: '⭐',
      sparkle: '✨',
      confetti: '🎉',
      leaf: '🍂',
      flower: '🌸',
      sun: '☀️',
      moon: '🌙',
    };
    
    // Bubble-Konfiguration - optimiert für Performance
    const fullConfigs = [
      { left: 10, size: 14, duration: 14, delay: 0 },
      { left: 30, size: 10, duration: 12, delay: 3 },
      { left: 50, size: 16, duration: 16, delay: 5 },
      { left: 70, size: 8, duration: 11, delay: 2 },
      { left: 90, size: 12, duration: 13, delay: 7 },
    ];
    
    // Mobile/Low-Power: nur 3 Bubbles
    const bubbleConfigs = (DevicePerformance.isMobile || DevicePerformance.isLowPower) 
      ? fullConfigs.slice(0, 3) 
      : fullConfigs;
    
    const fragment = document.createDocumentFragment();
    bubbleConfigs.forEach((config) => {
      const bubble = document.createElement('div');
      bubble.className = 'card-bubble';
      
      // Custom Icon oder Standard
      if (manualIcon === 'custom' && customIcon) {
        const img = document.createElement('img');
        img.src = customIcon;
        img.alt = '';
        img.style.cssText = 'width: 100%; height: 100%; object-fit: contain;';
        bubble.appendChild(img);
        bubble.style.background = 'transparent';
      } else if (manualIcon && iconMap[manualIcon]) {
        bubble.textContent = iconMap[manualIcon];
        bubble.style.background = 'transparent';
        bubble.style.display = 'flex';
        bubble.style.alignItems = 'center';
        bubble.style.justifyContent = 'center';
        bubble.style.fontSize = `${config.size}px`;
      }
      
      const adjustedDuration = config.duration * (100 / speed);
      bubble.style.cssText += `
        left: ${config.left}%;
        width: ${config.size}px;
        height: ${config.size}px;
        animation-duration: ${adjustedDuration}s;
        animation-delay: ${config.delay}s;
      `;
      fragment.appendChild(bubble);
    });
    container.appendChild(fragment);
    
    return () => { 
      container.innerHTML = ''; 
      bubblesCreated.current = false; 
    };
  }, [effectConfig]);
  
  return <div ref={containerRef} className="card-bubbles-container" aria-hidden="true" />;
});

// Card Snow Effect
export const CardSnowEffect = memo(function CardSnowEffect({ effectConfig = {} }) {
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const activeFlakes = useRef(0);
  
  useEffect(() => {
    if (!containerRef.current || DevicePerformance.prefersReducedMotion) return;
    
    const container = containerRef.current;
    const maxFlakes = DevicePerformance.isMobile || DevicePerformance.isLowPower ? 4 : 8;
    const intensity = Math.min(3, maxFlakes);
    const speed = effectConfig.speed || 50;
    const customIcon = effectConfig.customIconUrl;
    const manualIcon = effectConfig.manualIcon;
    
    // Icon-Mapping
    const iconMap = {
      snowflake: '❄️',
      bubble: '🫧',
      heart: '❤️',
      star: '⭐',
      sparkle: '✨',
      confetti: '🎉',
      leaf: '🍂',
      flower: '🌸',
      sun: '☀️',
      moon: '🌙',
    };
    
    const createSnowflake = () => {
      if (activeFlakes.current >= maxFlakes) return;
      activeFlakes.current++;
      
      const flake = document.createElement('div');
      flake.className = 'card-snowpoint';
      
      // Custom Icon oder Standard
      if (manualIcon === 'custom' && customIcon) {
        const img = document.createElement('img');
        img.src = customIcon;
        img.alt = '';
        img.style.cssText = 'width: 100%; height: 100%; object-fit: contain;';
        flake.appendChild(img);
      } else if (manualIcon && iconMap[manualIcon]) {
        flake.textContent = iconMap[manualIcon];
      }
      
      const size = 2 + Math.random() * 3;
      const left = Math.random() * 100;
      const baseDuration = 4 + Math.random() * 3;
      const adjustedDuration = baseDuration * (100 / speed);
      
      flake.style.cssText = `
        left: ${left}%;
        width: ${size}px;
        height: ${size}px;
        animation-duration: ${adjustedDuration}s;
      `;
      
      container.appendChild(flake);
      
      setTimeout(() => {
        if (flake.parentNode) {
          flake.remove();
          activeFlakes.current--;
        }
      }, adjustedDuration * 1000);
    };
    
    // Initiale Flocken - gestaffelt
    for (let i = 0; i < intensity; i++) {
      setTimeout(createSnowflake, i * 400);
    }
    
    // Kontinuierlich neue Flocken
    const spawnInterval = DevicePerformance.isMobile ? 1500 : 1000;
    animationRef.current = setInterval(createSnowflake, spawnInterval);
    
    return () => {
      if (animationRef.current) clearInterval(animationRef.current);
      container.innerHTML = '';
      activeFlakes.current = 0;
    };
  }, [effectConfig]);
  
  return (
    <div ref={containerRef} className="card-snow-container" aria-hidden="true">
      <div className="card-snow-wave" />
    </div>
  );
});
